import React, { useState, useEffect, useRef } from 'react'
import ApiClient from "../bff-client/src/ApiClient";
import CustomersApi from "../bff-client/src/api/CustomersApi";
import SubscriptionsApi from "../bff-client/src/api/SubscriptionsApi";
import OrdersApi from "../bff-client/src/api/OrdersApi";
import AppConfig from '../Configuration/AppConfig';
import ActivationSuccess from './ActivationSuccess';
import CallToAction from './CallToAction';
import AsyncStorage from '@react-native-async-storage/async-storage'

import {
  StyleSheet,
  View,
  Animated,
  RefreshControl,
  ScrollView,
  Platform,
  Linking,
  Text,
  Dimensions,
  Image,
  ActivityIndicator
} from 'react-native'

import { useSafeAreaInsets } from 'react-native-safe-area-context'

import cloneDeep from 'lodash/cloneDeep'

import { useContent } from '@sart/content'

import client, { useClient } from '@sart/client'
import {
  adaptive,
  colors,
  backgroundColors,
  typography,
  radius,
  shadows
} from '@sart/styles'
import {
  Space,
  MoonDial,
  ProductAddon,
  ProductRecurring,
  PendingCashPayment,
  BasePlan,
  ActivationPrompt
} from '@sart/components'
import {
  CompanyIcon, Mobile
} from '@sart/icons'
import {
  prepaidBalanceContainer
} from '@sart/utilities'
import delay from 'delay';


const BRANDING_HEIGHT = 24
const MAX_DIALS_HEIGHT = 120
const MIN_DIALS_HEIGHT = 48
const EXPIRATION_HEIGHT = 24

export default function PrepaidDashboard(props) {
  const { account, service, navigation, reloadData } = props
  console.log('recently activated from props is: ' + props?.recentlyActivated)
  const content = useContent()
  const [data, setData] = useState({})

  const balanceContainer = cloneDeep(prepaidBalanceContainer)
  const [mainBalance, setMainBalance] = useState(false)
  const [activated, setActivated] = useState(false) //assume activated to hide the prompt until we check
  const [activationProcessing, setActivationProcessing] = useState(false)
  const [activationProduct, setActivationProduct] = useState(undefined)
  const [autoRenewEnabled, setAutoRenewEnabled] = useState(true) //assume auto renew to be true as the state does not get recorded until we first update the BFF
  const [recentlyActivated, setRecentlyActivated] = useState(props?.recentlyActivated || false)
  const [activationError, setActivationError] = useState(null)
  const [portInPromptDismissed, setPortInPromptDismissed] = useState('true')
  const [installESIMPromptDismissed, setInstallESIMPromptDismissed] = useState(Platform.OS === 'ios' ? 'false' : 'true')
  const [subscriptionDiscounts, setSubscriptionDiscounts] = useState(null)
  const [pSim, setPSim] = useState(false)
  const [pendingPlanChange, setPendingPlanChange] = useState(false)

  const APPLE_INSTALL_LINK = 'https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=';

  // let eSimInstallLink = ''

  // if (Platform.OS === 'android') {
  //   eSimInstallLink = `${ANDROID_INSTALL_LINK}${encodeURIComponent(activationCode)};end;`;
  //   const canOpen = Linking.canOpenURL(eSimInstallLink);
  //   if (!canOpen) {
  //     eSimInstallLink = `lpa:${activationCode}`
  //   }
  // } else {
  //   eSimInstallLink = APPLE_INSTALL_LINK + activationCode
  // }

  const bffClient = new ApiClient(AppConfig.bffBasePath)
  bffClient.defaultHeaders = {
    ...bffClient.defaultHeaders,
    Origin: AppConfig.shopOriginUrl
  }
  const customersApi = new CustomersApi(bffClient)
  const subscriptionsApi = new SubscriptionsApi(bffClient)
  const ordersApi = new OrdersApi(bffClient)

  const {
    cache: {
      customer,
      balances,
      provisionedOneOffAddOns,
      provisionedRecurringAddOns,
      accounts,
      language,
      contextService,
      token,
      refreshDashboardData
    } = {}
  } = useClient()

  const activationCode = contextService?.primaryIdentifier
  const iOSInstallLink = activationCode ? `${APPLE_INSTALL_LINK}${activationCode}` : ''

  const insets = useSafeAreaInsets()

  useEffect(() => {
    loadCustomerData()
    loadBalancesAndInvoices()
    getActivationStatus()
  }, [reloadData])

  useEffect(() => {
    if (refreshDashboardData) {
      if (props.reloadAccounts) {
        console.log('executing reload accounts as if a pull down was done')
        props.reloadAccounts()
      }
    }
  }, [refreshDashboardData])

  useEffect(() => {
    //whenever the customer changes, check if they are activated and if they have auto renew enabled
    getActivationStatus()
    getAutoRenewStatus()
    getPromptDismissals()
    getSubscriptionDiscounts()
  }, [customer])

  useEffect(() => {
    console.log('customer on dashboard is: ' + JSON.stringify(customer, null, 2))
    if (!customer) {
      const checkStoredToken = async () => {
        const storedToken = await AsyncStorage.getItem('token')
        const userId = await AsyncStorage.getItem('userId')
        const customerId = await AsyncStorage.getItem('customerId')
        console.log('Stored token is: ' + token)
        console.log('Stored userId is: ' + userId)
        console.log('Stored customerId is: ' + customerId)

        if (storedToken && userId && customerId) {
          //we have a stored token so sign the customer in and sync their data
          await client.signInWithStoredToken()
          loadCustomerData()
          loadBalancesAndInvoices()
        } else {
          //No stored token found, redirecting to login
          await client.signOutAndForget()
          navigation.popToTop()
          navigation.navigate('Sign', { screen: 'SignIn' })
        }
      }
      checkStoredToken()
    }
    //get the auto renew status on first login/load
    getAutoRenewStatus()
    getSubscriptionDiscounts();
  }, [])

  async function getPromptDismissals() {

    if (!customer || !customer.user) {
      //return and wait for the re-render methods to call this again once the customer has loaded
      return
    }

    AsyncStorage.getItem('portInPromptDismissed_' + customer.user.userName).then((value) => {
      setPortInPromptDismissed(value)
    })
    if (Platform.OS === 'ios') {
      AsyncStorage.getItem('installESIMPromptDismissed_' + customer.user.userName).then((value) => {
        if (value === null) {
          setInstallESIMPromptDismissed('false')
        } else {
          setInstallESIMPromptDismissed(value)
        }
      })
    } else {
      setInstallESIMPromptDismissed('true')
    }
  }

  async function getActivationStatus() {
    try {
      console.log('getting activation status, customer is: ' + JSON.stringify(customer, null, 2))
      const email = customer.user.userName
      const subscriptionStatusResp = await customersApi.customersSubscriptionsPendingGet(email)
      console.log('subscriptionStatusResp is: ' + JSON.stringify(subscriptionStatusResp, null, 2))
      if (subscriptionStatusResp) {
        setPSim(subscriptionStatusResp.pSim)
      }
      if (subscriptionStatusResp && subscriptionStatusResp.activated !== undefined) {
        setActivated(subscriptionStatusResp.activated)
        client.setCache({
          activated: subscriptionStatusResp.activated
        })
      } else {
        //No response from the BFF pending API so assume not activated
        setActivated(false)
        client.setCache({
          activated: false
        })
      }

    } catch (error) {
      console.log('error getting activation status: ', error)
      setActivated(false)
    }
  }

  async function getAutoRenewStatus() {
    try {
      const email = customer.user.userName
      const autoRenewResp = await customersApi.customersSubscriptionGet(email)
      // console.log('autoRenewResp is: ' + JSON.stringify(autoRenewResp, null, 2))
      if (Object.keys(autoRenewResp).length === 0) {
        //empty response so auto renew is on as default
        setAutoRenewEnabled(true)
      } else if (autoRenewResp.autoRenew !== undefined) {
        console.log('auto renew status is: ' + JSON.stringify(autoRenewResp))
        setAutoRenewEnabled(autoRenewResp.autoRenew)
      } else {
        setAutoRenewEnabled(false)
      }

    } catch (error) {
      console.log('error getting auto renew status: ', error)
    }

    //Always update the cache with the auto renew value
    client.setCache({
      autoRenewEnabled: autoRenewEnabled
    })
  }

  async function getSubscriptionDiscounts() {
    try {
      //firstly check for any pending plan changes, if we have them we will not show the discounts
      await checkPendingChangePlan()

      //if we have a pending plan change then don't bother getting the discounts as they are no longer valid
      if (pendingPlanChange) {
        setSubscriptionDiscounts(null)
        client.setCache({
          subscriptionDiscounts: null
        })
        return
      }

      const email = customer.user.userName
      const subscriptionDiscountsResp = await subscriptionsApi.subscriptionsPromoCodesGet(email)
      console.log('subscriptionDiscountsResp is: ' + JSON.stringify(subscriptionDiscountsResp, null, 2))

      if (subscriptionDiscountsResp?.promoCodes?.length > 0) {
        setSubscriptionDiscounts(subscriptionDiscountsResp.promoCodes)
        client.setCache({
          subscriptionDiscounts: subscriptionDiscountsResp.promoCodes
        })
      } else {
        setSubscriptionDiscounts(null)
        client.setCache({
          subscriptionDiscounts: null
        })
      }
    } catch (error) {
      console.log('error getting auto renew status: ', error)
    }
  }

  async function checkPendingChangePlan() {
    const emailAddress = customer?.user?.userName || ''
    if (!emailAddress) {
      return
    }

    try {
      const response = await bffClient.callApi(
        '/customers/subscriptions/change-plan', 'GET',
        {}, { email: emailAddress }, {}, {}, null,
        [], [], ['application/json'], Object, null
      )
      console.log('pending plan change response is: ' + JSON.stringify(response, null, 2))
      const statusCode = response?.response?.status
      const pendingData = response?.data
      const hasPendingMessage = typeof pendingData?.message === 'string' && pendingData.message.trim().length > 0
      const shouldShowPendingChange = statusCode === 200 && hasPendingMessage
      setPendingPlanChange(shouldShowPendingChange)
    } catch (pendingError) {
      // Any non-200 should keep the existing change-plan screen flow.
      setPendingPlanChange(false)
    }
  }

  async function activateSubscription() {
    setActivationProcessing(true)
    setActivationError(null)
    const email = customer.user.userName

    try {
      const subscriptionStatusResp = await customersApi.customersSubscriptionsPendingGet(email)
      console.log('subscriptionStatusResp is: ' + JSON.stringify(subscriptionStatusResp, null, 2))

      if (subscriptionStatusResp?.pSim) {
        //go to the Activate ICCID screen
        setActivationProcessing(false)
        navigation.navigate('ActivateICCID', { email })
        return
      }

      const productId = subscriptionStatusResp.productId

      const extendedData = {
        stripeCustomerId: subscriptionStatusResp.gatewayCustomerId,
        pSim: subscriptionStatusResp.pSim,
        promoCode: subscriptionStatusResp.promoCode
      }

      //Need to add the token to the header for the activation
      bffClient.defaultHeaders['Authorization'] = `Bearer ${token}`;

      // const activationResponse = await client.activateService(customer.customerId, extendedData, activationProduct, productId, 'hostedPayment')
      console.log('activating customer, id: ', customer.customerId)
      const activationResponse = await customersApi.customersIdActivatePost(customer.customerId)
      console.log('activationResponse is: ' + JSON.stringify(activationResponse, null, 2))

      /*
      {
        "basketRefId": "BRF-0001234",
        "gatewayURL": "https://checkout.stripe.com/pay/cs_test_..."
      }
      */

      const basketRefId = activationResponse?.basketRefId
      console.log('basketRefId is: ' + basketRefId)

      if (!basketRefId) {
        //order failed, throw an exception to be caught
        throw new Error(content.menu.activationFailed)
      }

      await delay(5000)

      //we need to remove the Authorization header from the bff client AND the ordersApi to be safe for the below api call
      bffClient.defaultHeaders['Authorization'] = undefined;
      const freshOrdersApi = new OrdersApi(bffClient)
      console.log('bff client default headers: ' + JSON.stringify(bffClient.defaultHeaders, null, 2))
      console.log('old ordersApi default headers: ' + JSON.stringify(ordersApi.apiClient.defaultHeaders, null, 2))
      console.log('fresh orders client default headers: ' + JSON.stringify(freshOrdersApi.apiClient.defaultHeaders, null, 2))
      console.log('getting activation order with basket ref Id: ', basketRefId)
      let activationOrder = await freshOrdersApi.ordersGet(basketRefId)

      if (!activationOrder) {
        throw new Error(content.menu.activationFailed)
      }
      console.log('activationOrder is: ' + JSON.stringify(activationOrder, null, 2))

      if (activationOrder.status === "successful") {
        //Only update the BFF if we have an actual success message from the activation
        setActivated(true)
        setRecentlyActivated(true)
        const activationBffResponse = await customersApi.customersSubscriptionsActivatePost({ email })
        console.log('waiting 2 seconds then trying to set reload data')
        await delay(2000)
        if (props.reloadAccounts) {
          console.log('reloading accounts via props as if done by pulldown')
          props.reloadAccounts()
        } else {
          console.log('reloading accounts via client cache')
          await client.cacheUserAccountInformation(client.cache.customerId)
          await client.cacheUserInformation(client.cache.customerId, 'Mobile')
          await loadBalancesAndInvoices()

        }



        // console.log('waiting 2nd time for 8 seconds and then trying to get balances')
        // await delay(8000)
        // await loadBalancesAndInvoices()

        // console.log('waiting 3rd time for 8 seconds and then trying to get balances')
        // await delay(8000)
        // await loadBalancesAndInvoices()

      }
    } catch (error) {
      console.log('error activating: ', error)
      setActivated(false)
      setActivationError('There was a problem activating your plan. Please try again later and if the problem persists contact support.')
    }
    //then refresh our BFF status
    getActivationStatus()
    setActivationProcessing(false)
  }

  async function loadBalancesAndInvoices() {
    try {
      setData({
        loading: true
      })
      console.log('LOADING BALANCES AND INVOICES IN PREPAID DASHBOARD with account: ', JSON.stringify(account, null, 2))
      const loadedResult = await client.loadBalancesAndInvoices(account)
      console.log('loaded result is: ' + JSON.stringify(loadedResult, null, 2))
      const bundleProducts = loadedResult?.provisionedRecurringAddOns?.filter((addOn) => {
        const { tags } = addOn.product;
        return tags.find(tag => tag.tag === "package")
      })
      // const planProducts = await client.callApi('service', 'getServicePlanProductInstances',[account.accountId, service != undefined ? service.serviceId : account.services[0].serviceId])
      console.log('bundleProducts: ' + JSON.stringify(bundleProducts, null, 2));
      setData({
        planProducts: bundleProducts,
        loading: false
      })

      if (client.cache.serviceId === undefined) {
        client.setCache({
          serviceId: service.serviceId
        })
      }
    } catch (error) {
      console.log('error when loading balances and invoices: ', error)
      setData({ loading: false })
    }
  }

  function getCategorisedAddOns(addOns = []) {
    // console.log('addOns is: ' + JSON.stringify(addOns, null, 2));
    const oneoff = addOns.filter((addOn) => {
      // console.log('addOn.product is: ' + JSON.stringify(addOn.product.recurring, null, 2));
      return addOn.product.recurring === false
    })

    const recurring = addOns.filter((addOn) => {
      return addOn.product.recurring === true
    })

    return { oneoff, recurring }
  }
  function loadCustomerData() {
    //If the customer has not been loaded yet, load it
    //Mainly here for web app refresh issue SRPRODDEV-386
    console.log('loading customer data')
    if (customer === undefined) {
      client.loadPersonalDetails()
    }
  }

  function cancelRecurringAddOn() {

  }

  const MAX_HEADER_HEIGHT = BRANDING_HEIGHT + MAX_DIALS_HEIGHT + EXPIRATION_HEIGHT
  const MIN_HEADER_HEIGHT = MIN_DIALS_HEIGHT
  const HEADER_HEIGHT_DELTA = MAX_HEADER_HEIGHT - MIN_HEADER_HEIGHT

  const scrollY = new Animated.Value(0)

  const headerY = scrollY.interpolate({
    inputRange: [0, HEADER_HEIGHT_DELTA],
    outputRange: [0, 1],
    extrapolateRight: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const translateY = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 36],
    extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const headerHeight = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [MAX_HEADER_HEIGHT, MIN_HEADER_HEIGHT],
    extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
  })

  const spacerHeight = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [0, HEADER_HEIGHT_DELTA],
  })

  const hide = headerY.interpolate({
    inputRange: [0, 0.7],
    outputRange: [1, 0],
  })

  const show = headerY.interpolate({
    inputRange: [0.7, 1],
    outputRange: [0, 1],
  })

  const smallDialSize = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [106, 48],
    extrapolate: 'clamp'
  })

  const bigDialSize = headerY.interpolate({
    inputRange: [0, 1],
    outputRange: [125, 48],
    extrapolate: 'clamp'
  })

  const UNITS = [
    'B',
    'KB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB'
  ];

  function getBalanceQuantity(balance) {
    if (balance !== undefined) {
      return balance.quantity !== undefined ? balance.quantity : 0
    }
    return 0
  }

  function getBalanceStartingQuantity(balance) {
    if (balance !== undefined) {
      return balance.startingQuantity !== undefined ? balance.startingQuantity : 0
    }
    if (balance < 0) {
      return balance.startingQuantity < 0 ? balance.startingQuantity : 0
    }
    return 0
  }

  function formatBytes(value, balances, options) {
    if (!Number.isFinite(value)) {
      throw new TypeError(`Expected a finite number, got ${typeof value}: ${value}`);
    }
    options = Object.assign({}, options);

    if (value === 0) {
      return ' 0B';
    }

    const isNegative = value < 0;
    const prefix = isNegative ? '-' : '';

    if (isNegative) {
      return value;
    }

    if (value < 1) {
      const numberString = toLocaleString(value, options.locale);
      return prefix + numberString + 'B';
    }
    // const exponent = Math.min((Math.floor(Math.log10(value) / 3) + 1), UNITS.length - 1);
    // Setting the exponent to 3 keeps the units to GB, the line above displays GB if above 1 GB and if belowm shows MB unit.
    const exponent = 3;
    const unit = UNITS[exponent];
    value = Number((value / getConversionRateData(balances, unit)).toFixed(2));
    const numberString = toLocaleString(value, options.locale);

    return prefix + numberString + '' + unit;

  }
  /*
  Formats the given number using `Number#toLocaleString`.
  - If locale is a string, the value is expected to be a locale-key (for example: `de`).
  - If locale is true, the system default locale is used for translation.
  - If no value for locale is specified, the number is returned unmodified.
  */
  const toLocaleString = (number, locale) => {
    let result = number;
    if (typeof locale === 'string') {
      result = number.toLocaleString(locale);
    } else if (locale === true) {
      result = number.toLocaleString();
    }

    return result;
  };

  function getConversionRateData(balances, unit) {
    if (balances?.data?.balanceDefinition?.conversionRates) {
      const conversionRates = balances.data.balanceDefinition.conversionRates
      const rate = conversionRates.filter(function (rate) {
        if (unit === 'GB') {
          return rate.unit === 'GB'
        }
        if (unit === 'MB') {
          return rate.unit === 'MB'
        }
      })
      return rate[0].rate
    } else {
      return 1024
    }
  }

  function convertToMsisdnFormat(msisdn) {
    if (msisdn && msisdn.startsWith('44')) {
      return '0' + msisdn.substring(2)
    }
    return msisdn
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.headerContainer}>
          <Animated.View
            style={[styles.headerContent, { height: headerHeight }]}
          >
            <Animated.View
              style={[styles.branding, { transform: [{ translateY: translateY }] }]}
            >
            </Animated.View>
            {Platform.OS === 'ios' &&
              <Space height={24} />
            }
            <View style={styles.rowContainer}>
              <View style={{ justifyContent: 'flex-end' }}>

                {/* <CompanyIcon color={colors.srGreen} height={60} width={60} /> */}
              </View>
            </View>

            <Animated.View style={styles.headerMain}>
              <View>
                {accounts && accounts[0] && accounts[0].services[0] &&
                  <View>
                    <Space height={12} />
                    <View style={styles.numberContainer}>
                      <Image
                        source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
                        style={styles.logoImage}
                      />
                      <Text style={styles.numberText}>{convertToMsisdnFormat(service.lineIdentifier)}</Text>
                    </View>
                    <Space height={12} />
                  </View>
                }
                <Text style={styles.subtitle}>Remaining Allowances</Text>
                <Animated.View style={[styles.dials, { height: bigDialSize }]}>
                  <MoonDial
                    name={content.navigation.texts}
                    size={smallDialSize}
                    value={getBalanceQuantity(balances.text)}
                    range={getBalanceStartingQuantity(balances.text)}
                    loading={data.loading}
                    // onPress={() => void navigation.navigate('Texts')}
                    style={styles.moondialStyle}
                    strokeColour={colors.brightOrange + 20}
                    mostlyFullColour={colors.brightOrange}
                    almostEmptyColour={colors.brightOrange}
                    mostlyFullTextColour={colors.white}
                    almostEmptyTextColour={colors.white}
                    textColour={colors.white}
                    injectedContent={content}
                  />

                  <Space width={12} />

                  <MoonDial
                    name={content.navigation.data}
                    formatValue={val => formatBytes(val, balances)}
                    formatRange={val => formatBytes(val, balances)}
                    size={bigDialSize}
                    value={getBalanceQuantity(balances.data)}
                    range={getBalanceStartingQuantity(balances.data)}
                    loading={data.loading}
                    // onPress={() => void navigation.navigate('Data')}
                    style={styles.moondialStyle}
                    strokeColour={colors.brightOrange + 20}
                    mostlyFullColour={colors.brightOrange}
                    almostEmptyColour={colors.brightOrange}
                    mostlyFullTextColour={colors.white}
                    almostEmptyTextColour={colors.white}
                    textColour={colors.white}
                    injectedContent={content}
                  />

                  <Space width={12} />

                  <MoonDial
                    name={content.navigation.minutes}
                    size={smallDialSize}
                    value={getBalanceQuantity(balances.voice)}
                    range={getBalanceStartingQuantity(balances.voice)}
                    loading={data.loading}
                    // onPress={() => void navigation.navigate('Minutes')}
                    style={styles.moondialStyle}
                    strokeColour={colors.brightOrange + 20}
                    mostlyFullColour={colors.brightOrange}
                    almostEmptyColour={colors.brightOrange}
                    mostlyFullTextColour={colors.white}
                    almostEmptyTextColour={colors.white}
                    textColour={colors.white}
                    injectedContent={content}
                  />
                </Animated.View>
              </View>
            </Animated.View>

          </Animated.View>

          <Animated.View style={[styles.line, { opacity: show }]} />
        </View>
        <Animated.ScrollView
          scrollEventThrottle={1}
          // stickyHeaderIndices={[0,]}
          showsVerticalScrollIndicator={true}
          contentContainerStyle={styles.contentContainer}
          onScroll={
            Animated.event([
              { nativeEvent: { contentOffset: { y: scrollY } } }
            ], {})
          }
        >
          <Space height={12} />
          {/* <Animated.View style={{ height: spacerHeight }} /> */}

          <View style={styles.content}>
            {(recentlyActivated && !data.planProducts?.length) &&
              <ActivationSuccess color={colors.brightOrange} textColor={colors.brightOrange} />
            }
            {data.loading &&
              <ActivityIndicator color={colors.brightOrange} size={48} />
            }
            {(!activated && !data.loading) &&
              <>
                <ActivationPrompt
                  titleStyle={styles.titleStyle}
                  cashContainer={styles.cashContainer}
                  priceStyle={styles.priceStyle}
                  descriptionStyle={styles.descriptionStyle}
                  descriptionContentStyle={styles.descriptionContentStyle}
                  dateStyle={styles.dateStyle}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  disabledButtonStyle={styles.disabledButton}
                  price={"£" + 0}
                  onPress={activateSubscription}
                  language={language}
                  injectedContent={content}
                  disabled={activationProcessing}
                  processing={activationProcessing}
                  errorMessage={activationError}
                />
                <Space height={24} />
              </>
            }

            {(!data.loading && data.planProducts?.length === 0 && activated) &&
              <>
                <CallToAction
                  titleStyle={styles.titleStyle}
                  containerStyle={styles.cashContainer}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  disabledButtonStyle={styles.disabledButton}
                  // navigate to the Change Plan screen in the Menu stack (switch to Menu tab)
                  // Use a one-time pending flag so we don't permanently mutate the tab navigator's nested state
                  onPress={() => {
                    try {
                      client.setCache({ pendingMenuScreen: 'ChangePlan' })
                    } catch (err) {
                      // ignore cache errors
                    }
                    navigation.navigate('MenuHome')
                  }}
                  text='You do not currently have a plan, to get the most out of your Biker Mobile service you can add one using the button below'//{content.dashboard.changePlanPrompt}
                  buttonText='Purchase Plan'//{content.dashboard.changePlan}
                />
                <Space height={24} />
              </>
            }

            {Platform.OS === 'ios' && !data.loading && installESIMPromptDismissed !== 'true' && activated && !pSim &&
              <>
                <CallToAction
                  titleStyle={styles.titleStyle}
                  containerStyle={styles.cashContainer}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  disabledButtonStyle={styles.disabledButton}
                  onPress={async () => {
                    try {
                      if (!iOSInstallLink) {
                        client.setCache({ pendingMenuScreen: 'ManageSubscription' })
                        navigation.navigate('MenuHome')
                        return
                      }
                      await Linking.openURL(iOSInstallLink)
                    } catch (err) {
                      try {
                        client.setCache({ pendingMenuScreen: 'ManageSubscription' })
                      } catch (cacheErr) {
                        // ignore cache errors
                      }
                      navigation.navigate('MenuHome')
                    }
                  }}
                  text='Install your eSIM'//{content.dashboard.changePlanPrompt}
                  buttonText='Install eSIM'//{content.dashboard.changePlan}
                  enableDismiss={true}
                  dismissFunction={() => {
                    try {
                      AsyncStorage.setItem('installESIMPromptDismissed_' + customer.user.userName, 'true');
                      setInstallESIMPromptDismissed('true');
                    } catch (error) {
                      // ignore errors
                    }
                  }}
                />
                <Space height={24} />

              </>
            }
            {(!data.loading && portInPromptDismissed !== 'true' && activated) &&
              <>
                <CallToAction
                  titleStyle={styles.titleStyle}
                  containerStyle={styles.cashContainer}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  disabledButtonStyle={styles.disabledButton}
                  // navigate to the Port In screen in the Menu stack (switch to Menu tab)
                  // Use a one-time pending flag so we don't permanently mutate the tab navigator's nested state
                  onPress={() => {
                    try {
                      //We're currently doing nothing here as the install eSIM functionality isn't working
                      client.setCache({ pendingMenuScreen: 'Switch' })
                    } catch (err) {
                      // ignore cache errors
                    }
                    navigation.navigate('MenuHome')
                  }}
                  text='Port in your number'//{content.dashboard.changePlanPrompt}
                  buttonText='Port In'//{content.dashboard.changePlan}
                  enableDismiss={true}
                  dismissFunction={() => {
                    try {
                      AsyncStorage.setItem('portInPromptDismissed_' + customer.user.userName, 'true');
                      setPortInPromptDismissed('true');
                    } catch (error) {
                      // ignore errors
                    }
                  }}
                />
                <Space height={24} />
              </>
            }
            <View>
              {(!data.loading && data.planProducts) && data.planProducts.map((bundle, i) =>
                <React.Fragment key={i}>

                  <BasePlan
                    product={bundle}
                    productContainer={styles.planContainer}
                    labelStyle={styles.descriptionStyle}
                    nameStyle={styles.descriptionContentStyle}
                    textStyle={styles.dateStyle}
                    buttonStyle={styles.buttonStyle}
                    productName={bundle.product.productName}
                    price={bundle.product.upfrontCost}
                    language={language}
                    injectedContent={content}
                    code={bundle.product.currency}
                    showChange={false}
                    switchPlan={() => void navigation.navigate('ManageSubscription')}
                    toggleValue={autoRenewEnabled}
                    discounts={subscriptionDiscounts}
                  />
                  <Space height={24} />
                </React.Fragment>
              )}
            </View>

            {/* {provisionedRecurringAddOns.map((addOn, i) =>
              <React.Fragment key={i}>
                <ProductRecurring
                  product={{
                    type: 'plan',
                    name: addOn.product.productName,
                    price: addOn.product.recurringCost,
                    expiration: addOn.expiryDateTime,
                    startDate: addOn.startDateTime,
                    category: 'recurring',
                    code: addOn.product.currency,
                    frequency: addOn.product.recurringFrequency,
                    frequencyUnit: addOn.product.recurringFrequencyUnit,
                  }}
                  onEditProduct={() => void navigation.navigate('Plans')}
                  onCancelProduct={() => { cancelRecurringAddOn(addOn) }}
                  productContainer={styles.cashContainer}
                  labelStyle={styles.descriptionStyle}
                  nameStyle={styles.descriptionContentStyle}
                  textStyle={styles.dateStyle}
                  buttonTitleStyle={styles.buttonTitleStyle}
                  buttonStyle={styles.buttonStyle}
                  cancelStyle={styles.cancelStyle}
                  addOn={addOn}
                  language={language}
                  injectedContent={content}
                />
                <Space height={24} />
              </React.Fragment>
            )} */}

            {(!data.loading && provisionedOneOffAddOns) && provisionedOneOffAddOns.map((addOn, i) =>
              <React.Fragment key={i}>
                <ProductAddon
                  iconColor={colors.white}
                  style={styles.addonStyle}
                  placeholderStyle={styles.balanceItem}
                  disabled={true}
                  product={{
                    ...addOn.product,
                    expiration: addOn.expiryDateTime
                  }}
                  addOn
                  language={language}
                  injectedContent={content}
                />
                <Space height={24} />
              </React.Fragment>
            )}
            <Space height={48} />

          </View>
        </Animated.ScrollView>
      </View>
    </View>

  )
}

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    width: Dimensions.get('window').width,
    // paddingTop: 10,
    ...Platform.select({
      web: {
        alignItems: 'center'
      }
    })
  },
  contentContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    // justifyContent: 'center',
  },
  headerContainer: {
    justifyContent: 'center',
    height: 250,
    backgroundColor: colors.darkCharcoal,
  },
  headerContent: {
    justifyContent: 'center',
    ...adaptive.wrapperPadding,
    backgroundColor: colors.darkCharcoal,
    borderColor: colors.white,
    flex: 1
  },
  branding: {
    // height: BRANDING_HEIGHT,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dials: {
    // height: MAX_DIALS_HEIGHT,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: -1,
  },
  expiration: {
    height: EXPIRATION_HEIGHT,
  },
  description: {
    textAlign: 'center'
  },
  line: {
    borderColor: colors.subtle,
    borderWidth: StyleSheet.hairlineWidth,
  },
  list: {
    flex: 1,
    backgroundColor: backgroundColors.subtle,
    padding: 12
  },
  button: {
    backgroundColor: colors.srGreyBackground
  },
  content: {
    paddingTop: 12,
    ...adaptive.wrapperPadding,
    paddingBottom: 24,
    zIndex: 0
  },
  row: {
    flexDirection: 'row'
  },
  bold: {
    ...typography.bold
  },
  moondialStyle: {
    ...typography.lightbold,
  },
  // descriptionStyle: {
  //   ...typography.demi
  // },
  buttonStyle: {
    backgroundColor: backgroundColors.brightOrange,
    height: 48
  },
  disabledButton: {
    backgroundColor: backgroundColors.brightOrange + 60
  },
  titleStyle: {
    color: colors.white,
    ...typography.demi,
    fontSize: 16,
    lineHeight: 28,
    textAlign: 'center',
  },
  subtitle: {
    color: colors.white,
    ...typography.demi,
    fontSize: 16,
    lineHeight: 28,
    alignSelf: 'center',
    paddingBottom: 8
  },
  cashContainer: {
    padding: 24,
    paddingBottom: 12,
    paddingTop: 12,
    backgroundColor: backgroundColors.darkCharcoal,
    borderRadius: radius.medium,
    elevation: 8,
    borderColor: colors.white,
    borderWidth: 1
  },
  planContainer: {
    padding: 24,
    backgroundColor: backgroundColors.darkCharcoal,
    borderRadius: radius.medium,
    elevation: 8,
    // ...shadows.shadowByElevation(8),
    borderColor: colors.white,
    borderWidth: 1
  },
  priceStyle: {
    fontSize: 32,
    lineHeight: 48,
    fontWeight: '700',
    color: colors.white
  },
  descriptionStyle: {
    fontSize: 14,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi,
  },
  descriptionnNoPlanStyle: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.srTextInput,
    ...typography.demi,
  },
  descriptionContentStyle: {
    fontSize: 14,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi
  },
  dateStyle: {
    fontSize: 14,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi
  },
  buttonTitleStyle: {
    ...typography.lightbold,
    fontSize: 16
  },
  cancelStyle: {
    color: colors.white,
    textAlign: 'center'
  },
  noPlanContainerStyle: {
    backgroundColor: colors.white,
    ...adaptive.noPlanContainerHeight
  },
  addonStyle: {
    backgroundColor: backgroundColors.darkCharcoal,
    borderRadius: radius.medium,
    elevation: 8,
    // ...shadows.shadowByElevation(8),
    borderColor: colors.white,
    borderWidth: 1,
    height: 90
  },
  balanceItem: {
    backgroundColor: colors.brightOrange
  },
  moondialTextStyle: {
    ...typography.lightbold,
    color: colors.srTextInput
  },
  numberText: {
    textAlign: 'center',
    ...typography.bold,
    fontSize: 24,
    color: colors.white
  },
  headerMain: {
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  topUp: {
    backgroundColor: colors.srGreen
  },
  topUpText: {
    color: colors.white,
    ...typography.lightbold
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around'
  },
  amountDue: {
  },
  amountDueTitle: {
    textAlign: 'right',
    ...typography.bold,
    fontSize: 16,
    color: colors.srDarkGreen
  },
  amountDueText: {
    textAlign: 'right',
    ...typography.bold,
    fontSize: 20,
    color: colors.srDarkGreen
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  numberContainer: {
    flexDirection: 'row',
    justifyContent: 'center'
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative'
  }
})